import CrudTable from "components/table/CrudTable";
import { render } from "katex";
import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import {
  createTimeTableTemplate,
  deleteTimeTableTemplate,
  fetchAcademicyears,
  fetchClassSections,
  fetchTimeTableTemplates,
  updateTimeTableTemplate,
} from "ServerAPI/serverAPI";
import ManageTimeTableForm from "./ManageTimeTableForm";
import { useToast } from "context/toast-context/ToastProvider";
import ToastMessage from "components/Dialog/ToastMessage";

const ManageTimeTable = () => {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [timetableRows, setTimetableRows] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classSections, setClassSections] = useState([]);
  const [showToastMessage, setShowToastMessage] = useState(false);

  const [cookies] = useCookies();
  const timetableColumns = [
    {
      field: "sno",
      headerName: "S.No",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      headerClassName: "table-header",
      editable: false,
    },
    {
      field: "className",
      headerName: "Section",
      flex: 1,
      headerClassName: "table-header",
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      headerClassName: "table-header",
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
    },
    {
      field: "academicYear",
      headerName: "Academic Year",
      flex: 1,
      headerClassName: "table-header",
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      headerClassName: "table-header",
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (params.value ? "Active" : "Inactive"),
    },
  ];

  const handleToastMessageClose = () => {
    setShowToastMessage(false);
  };

  const fetchData = async () => {
    setLoading(true);
    const [timetableRes, classSectionsRes, academicYearsRes] =
      await Promise.all([
        fetchTimeTableTemplates({ userId: cookies.userId }),
        fetchClassSections({ userId: cookies.userId }),
        fetchAcademicyears({ userId: cookies.userId }),
      ]);
    if (classSectionsRes?.status === "success") {
      setClassSections(classSectionsRes?.data || []);
      setAcademicYears(academicYearsRes?.data || []);
      const data = timetableRes?.data?.map((row) => ({
        ...row,
        timeTableId: row.timeTableId,
        className:
          classSectionsRes?.data?.find(
            (c) => c.classSectionId === row.classSectionId,
          )?.title || "-",
        department:
          classSectionsRes?.data?.find(
            (c) => c.classSectionId === row.classSectionId,
          )?.department || "-",
        academicYear:
          academicYearsRes?.data?.find(
            (a) => a.academicYearId === row.academicYearId,
          )?.name || "-",
        status:
          academicYearsRes?.data?.find(
            (a) => a.academicYearId === row.academicYearId,
          )?.isCurrent || "-",
      }));
      const rowsWithSno = (data || []).map((row, index) => ({
        ...row,
        sno: index + 1,
      }));
      setTimetableRows(rowsWithSno || []);
      setLoading(false);
    } else {
      setAcademicYears([]);
      setClassSections([]);
      setCoordinators([]);
      setTimetableRows([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [cookies.userId]);

  const handleCreateTimeTable = async (data) => {
    setButtonLoading(true);
    const payload = {
      ...data,
      userId: cookies.userId,
    };
    const response = await createTimeTableTemplate(payload);
    if (response && response?.status === "success") {
      const data = response?.data?.map((row) => ({
        ...row,
        timeTableId: row.timeTableId,
        className:
          classSections?.find((c) => c.classSectionId === row.classSectionId)
            ?.title || "-",
        department:
          classSections?.find((c) => c.classSectionId === row.classSectionId)
            ?.department || "-",
        academicYear:
          academicYears?.find((a) => a.academicYearId === row.academicYearId)
            ?.name || "-",
        status:
          academicYears?.find((a) => a.academicYearId === row.academicYearId)
            ?.isCurrent || "-",
      }));
      const rowsWithSno = (data || []).map((row, index) => ({
        ...row,
        sno: index + 1,
      }));
      setTimetableRows(rowsWithSno || []);
      setButtonLoading(false);
      showToast("Time Table Created Successfully", "success");
      return true;
    } else {
      setButtonLoading(false);
      showToast(response?.message || "Failed to create Time Table", "error");
      return false;
    }
  };

  const handleDeleteTimeTableTemplate = async (data) => {
    setLoading(true);
    const payload = {
      userId: cookies.userId,
      timeTableId: data.timeTableId,
    };
    const response = await deleteTimeTableTemplate(payload);
    if (response && response?.status === "success") {
      const data = response?.data?.map((row) => ({
        ...row,
        timeTableId: row.timeTableId,
        className:
          classSections?.find((c) => c.classSectionId === row.classSectionId)
            ?.title || "-",
        department:
          classSections?.find((c) => c.classSectionId === row.classSectionId)
            ?.department || "-",
        academicYear:
          academicYears?.find((a) => a.academicYearId === row.academicYearId)
            ?.name || "-",
        status:
          academicYears?.find((a) => a.academicYearId === row.academicYearId)
            ?.isCurrent || "-",
      }));
      const rowsWithSno = (data || []).map((row, index) => ({
        ...row,
        sno: index + 1,
      }));
      setTimetableRows(rowsWithSno || []);
      setLoading(false);
      showToast("Time Table Deleted Successfully", "success");
      setShowToastMessage(true);
    } else {
      setLoading(false);
      showToast(response?.message || "Failed to delete Time Table", "error");
    }
  };

  const handleUpdateTimeTableTemplate = async (data) => {
    setButtonLoading(true);
    const payload = {
      userId: cookies.userId,
      ...data,
    };
    const response = await updateTimeTableTemplate(payload);
    if (response && response?.status === "success") {
      const data = response?.data?.map((row) => ({
        ...row,
        timeTableId: row.timeTableId,
        className:
          classSections?.find((c) => c.classSectionId === row.classSectionId)
            ?.title || "-",
        department:
          classSections?.find((c) => c.classSectionId === row.classSectionId)
            ?.department || "-",
        academicYear:
          academicYears?.find((a) => a.academicYearId === row.academicYearId)
            ?.name || "-",
        status:
          academicYears?.find((a) => a.academicYearId === row.academicYearId)
            ?.isCurrent || "-",
      }));
      const rowsWithSno = (data || []).map((row, index) => ({
        ...row,
        sno: index + 1,
      }));
      setTimetableRows(rowsWithSno || []);
      setButtonLoading(false);
      showToast("Time Table Updated Successfully", "success");
      return true;
    } else {
      setButtonLoading(false);
      showToast(response?.message || "Failed to update Time Table", "error");
      return false;
    }
  };

  return (
    <div className="container ms-auto my-5">
      <ToastMessage
        open={showToastMessage}
        message="Course Marked As Deleted"
        closeTiming={3000}
        handleClose={handleToastMessageClose}
      />
      <CrudTable
        columns={timetableColumns}
        rows={timetableRows}
        loading={loading}
        onRefresh={fetchData}
        FormComponent={ManageTimeTableForm}
        handleSubmit={handleCreateTimeTable}
        buttonLoading={buttonLoading}
        handleDelete={handleDeleteTimeTableTemplate}
        onUpdate={handleUpdateTimeTableTemplate}
        getRowId={(row) => row.timeTableId}
        disable={cookies.roleId !== "organizationCoordinator"}
        formwidth={800}
        createButtonText="Create Time Table"
        academicYears={
          academicYears?.length > 0
            ? academicYears.find((a) => a.isCurrent)
            : {}
        }
        classSections={
          classSections?.length > 0
            ? classSections.filter(
                (c) =>
                  c.academicYear ===
                  academicYears?.find((a) => a.isCurrent)?.academicYearId,
              )
            : []
        }
      />
    </div>
  );
};

export default ManageTimeTable;
